/* eslint-disable @typescript-eslint/member-ordering */
import { ProjectsRootVisualStore, ErrorStore, RouterStore, UsersStore } from '../../common/stores';
import { ApplicationDefinitionsService, EnvironmentVariablesService } from '../services';
import { computed, action, observable, runInAction, reaction } from 'mobx';
import { InputBinding, EnvironmentVariable, InputMeta, BindingType, ApplicationFieldQueryDefinition } from '../types';
import { ApplicationDefinitionBase, ApplicationDefinition, InputFieldSettingDto } from '../types/ApplicationDefinition';
import { Dictionary } from 'lodash';
import { RulesStore } from '../../rules/stores';
import TagsGroupVisualStore from '../../rules/stores/TagsGroupVisualStore';
import ProjectApplicationDefinitionsVisualStore from './ProjectApplicationDefinitionsVisualStore';
import BindingPreviewVisualStore from './BindingPreviewVisualStore';
import { IotaAppPagesNavigation } from '../routes';
import { RcFile } from 'antd/lib/upload';
import { message } from 'antd';
import { ImportInputsResponse } from '../../common/types/types';
import { endpoints } from '../../../config';
import { AdditionalClassificationDto } from '../../application_definition_conditional/types';

type LocalBindings = {
    [key: string]: InputBinding[];
};

export type FormField = { type?: string; value?: string | null };
export type FieldSettings = {
    isVisible: boolean;
    isRequired: boolean;
    defaultIsRequired?: boolean;
    requiredOverride?: boolean;
};

type FieldSettingsMap = {
    [key: string]: FieldSettings;
};

export default class ProjectApplicationDefinitionEditVisualStore {
    @observable
    currentAppDef: ApplicationDefinitionBase | undefined;

    @observable
    inputs: InputMeta[] = [];

    @computed
    get fieldSettingsInputs(): InputMeta[] {
        const seenInputIds = new Set<string>();

        return this.inputs.filter(input => {
            if (seenInputIds.has(input.id)) {
                return false;
            }

            seenInputIds.add(input.id);
            return true;
        });
    }

    @observable
    initialBindings: InputBinding[] = [];

    @observable
    localBindings: LocalBindings = {};

    @observable
    fieldSettings: FieldSettingsMap = {};

    @observable
    isFieldSettingsDrawerOpen: boolean = false;

    @observable
    applicationQueryDefinitions: ApplicationFieldQueryDefinition[] = [];

    @observable
    isApplicationQueryDefinitionsLoading: boolean = false;

    @observable
    applicationQueryDefinitionsError: string | null = null;

    private applicationQueryDefinitionsApplicationId: string | null = null;

    private applicationQueryDefinitionsRequestId = 0;

    @observable
    environmentVariables: EnvironmentVariable[];

    @observable
    isLoading: boolean = false;

    @observable
    currentImportFile: RcFile | undefined = undefined;

    @observable
    fileImportActionHeaders: {};

    @observable
    isImportConfirmDialogVisible: boolean = false;

    @observable
    isImportingBindings: boolean = false;

    @observable
    isFormChanged: boolean = false;

    @computed
    get isInputDisabled() {
        return this.appDefStore.isInputDisabled && this.currentAppDef?.state === 'Published';
    }

    @computed
    get currentProject() {
        return this.rootStore.currentProject!;
    }

    @computed
    get tags() {
        return Object.getOwnPropertyNames(this.ruleStore.groupedByTag)
            .slice()
            .sort((a, b) => a.localeCompare(b));
    }

    @computed
    get ruleTags() {
        return this.ruleStore.ruleTags;
    }

    @computed
    get tagGroups() {
        return this.tagsGroupStore.tagsGroups ?? [];
    }

    @computed
    get importBindingsActionUrl() {
        if (!this.currentProject || !this.currentAppDef) {
            return '';
        }

        return (
            endpoints.manageUrl +
            `projects/${this.currentProject.id}/iota/applications/${this.currentAppDef.id}/import-bindings`
        );
    }

    @computed
    get users() {
        return this.usersStore.users;
    }

    @computed
    get isLoadingUsers() {
        return this.usersStore.isLoading;
    }

    constructor(
        private rootStore: ProjectsRootVisualStore,
        private appDefService: ApplicationDefinitionsService,
        private ruleStore: RulesStore,
        private tagsGroupStore: TagsGroupVisualStore,
        private envVariablesService: EnvironmentVariablesService,
        public errorStore: ErrorStore,
        private appDefStore: ProjectApplicationDefinitionsVisualStore,
        private routerStore: RouterStore,
        private previewStore: BindingPreviewVisualStore,
        private usersStore: UsersStore
    ) {
        this.loadEnvironmentVariables();
    }

    getUserNameById(id: string) {
        return this.usersStore.getUserNameById(id);
    }

    @action.bound
    async setHeaders() {
        const token = await this.appDefService.getAuthToken();
        this.fileImportActionHeaders = {
            Authorization: 'Bearer ' + token
        };
        return Promise.resolve();
    }

    @action.bound
    setCurrentImportFile(file: RcFile | undefined) {
        this.currentImportFile = file;
    }

    @action.bound
    getTagsForGroup(groupId: string | null) {
        return this.ruleStore.ruleTags.filter(x => x.groupId === groupId).map(y => y.name);
    }

    getTagById(id: string) {
        const tag = this.ruleStore.ruleTags.find(x => x.id === id);
        return (tag && tag.name) || '';
    }

    getTagIdByName(name: string) {
        const tag = this.ruleStore.ruleTags.find(x => x.name === name);
        return tag ? tag.id : '';
    }

    @action.bound
    async loadApplication(id: string) {
        this.resetApplicationQueryDefinitions();

        if (this.currentProject!) {
            this.isLoading = true;

            const promises = [
                this.getRules(),
                this.appDefService.getApplicationDefinition(this.currentProject.id, id)
            ] as [Promise<void>, Promise<ApplicationDefinitionBase | null>];

            const resp = await Promise.all(promises);
            const appDef = resp[1];

            runInAction(() => {
                this.currentAppDef = appDef!;

                if (appDef instanceof ApplicationDefinition) {
                    this.initialBindings = appDef!.bindings;
                }

                this.processMeta();
                this.closeFieldSettingsDrawer();
                this.setLocalBindings();
                this.isLoading = false;
            });
        } else {
            reaction(
                () => this.currentProject,
                (p, r) => {
                    this.loadApplication(id);
                    r.dispose();
                }
            );
        }
    }

    getRules() {
        if (!this.ruleStore.ruleTags?.length) {
            return this.ruleStore.getRules();
        }
        return Promise.resolve();
    }

    setLocalBindings() {
        this.localBindings = {};
        this.inputs.forEach(x => {
            const bindings = this.initialBindings === null ? [] : this.initialBindings.filter(y => y.inputId === x.id);
            if (bindings.length) {
                this.localBindings[x.id] = bindings;
            } else {
                this.localBindings[x.id] = [{ inputId: x.id, type: BindingType.constant, value: null }];
            }
        });
    }

    @action.bound
    setFieldSettings(inputId: string, settings: FieldSettings) {
        const existingSettings = this.fieldSettings[inputId];
        const input =
            this.fieldSettingsInputs.find(field => field.id === inputId) ??
            this.inputs.find(field => field.id === inputId);
        const defaultIsRequired =
            settings.defaultIsRequired ??
            existingSettings?.defaultIsRequired ??
            (input ? this.getInputDefaultRequired(input) : false);

        this.fieldSettings[inputId] = this.normalizeFieldSettings({
            ...existingSettings,
            ...settings,
            defaultIsRequired
        });
    }

    @action.bound
    syncAdditionalClassificationInputs(additionalClassifications: AdditionalClassificationDto[]) {
        const additionalSection = 'Additional Classifications';
        const retainedInputs = this.inputs.filter(input => input.section !== additionalSection);
        const generatedInputs = this.flattenAdditionalClassificationInputs(additionalClassifications);

        this.inputs = [...retainedInputs, ...generatedInputs];
        this.fieldSettings = this.fieldSettingsInputs.reduce<FieldSettingsMap>((acc, input) => {
            acc[input.id] = this.fieldSettings[input.id] ?? this.createInitialFieldSettings(input);
            return acc;
        }, {});
    }

    @action.bound
    openFieldSettingsDrawer(canLoadQueryDefinitions: boolean) {
        this.isFieldSettingsDrawerOpen = true;

        if (canLoadQueryDefinitions) {
            void this.loadApplicationQueryDefinitions();
        } else {
            this.resetApplicationQueryDefinitions();
        }
    }

    @action.bound
    closeFieldSettingsDrawer() {
        this.isFieldSettingsDrawerOpen = false;
    }

    getApplicationQueryDefinition(inputId: string): ApplicationFieldQueryDefinition | undefined {
        return this.applicationQueryDefinitions.find(definition => definition.inputId === inputId);
    }

    @action.bound
    async loadApplicationQueryDefinitions(forceReload: boolean = false): Promise<void> {
        const applicationId = this.currentAppDef?.id;
        const projectId = this.rootStore.currentProject?.id;

        if (!applicationId || !projectId) {
            return;
        }

        const hasCachedResult = this.applicationQueryDefinitionsApplicationId === applicationId;

        if (!forceReload && (hasCachedResult || this.isApplicationQueryDefinitionsLoading)) {
            return;
        }

        const requestId = ++this.applicationQueryDefinitionsRequestId;
        this.isApplicationQueryDefinitionsLoading = true;
        this.applicationQueryDefinitionsError = null;

        try {
            const definitions = await this.appDefService.getApplicationQueryDefinitions(projectId, applicationId);

            if (requestId !== this.applicationQueryDefinitionsRequestId || this.currentAppDef?.id !== applicationId) {
                return;
            }

            runInAction(() => {
                this.applicationQueryDefinitions = definitions;
                this.applicationQueryDefinitionsApplicationId = applicationId;
                this.isApplicationQueryDefinitionsLoading = false;
            });
        } catch {
            if (requestId !== this.applicationQueryDefinitionsRequestId || this.currentAppDef?.id !== applicationId) {
                return;
            }

            runInAction(() => {
                this.applicationQueryDefinitions = [];
                this.applicationQueryDefinitionsApplicationId = null;
                this.applicationQueryDefinitionsError = 'Unable to load SQL query definitions.';
                this.isApplicationQueryDefinitionsLoading = false;
            });
        }
    }

    @action.bound
    retryApplicationQueryDefinitions(): void {
        void this.loadApplicationQueryDefinitions(true);
    }

    getFieldSettings(input: InputMeta): FieldSettings {
        return this.fieldSettings[input.id] ?? this.createInitialFieldSettings(input);
    }

    getFieldSettingsPayload(): InputFieldSettingDto[] {
        return this.fieldSettingsInputs.reduce<InputFieldSettingDto[]>((acc, input) => {
            const settings = this.getFieldSettings(input);

            if (settings.isVisible && settings.requiredOverride === undefined) {
                return acc;
            }

            acc.push({
                inputId: input.id,
                hidden: settings.isVisible ? undefined : true,
                required: settings.isVisible ? settings.requiredOverride : undefined
            });

            return acc;
        }, []);
    }

    @action.bound
    setEnvironmentVariables(envVars: EnvironmentVariable[]) {
        this.environmentVariables = envVars;
    }

    @action.bound
    async loadEnvironmentVariables() {
        const envVars = await this.envVariablesService.getAllEnvironmentVariables();
        this.setEnvironmentVariables(envVars);
    }

    @action
    updateLocalBindings(inputId: string, fields: Array<FormField>) {
        if (this.localBindings[inputId]) {
            this.localBindings[inputId] = fields.map(field => this.createInputBinding(inputId, field));
        }
    }

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async setMatch(m: any) {
        const ignorePaths = ['bindingPreview', 'applicationPreview', 'applicationDefinitionConditionalPreview'];

        if (this.routerStore.prevPathname && ignorePaths.some(path => this.routerStore.prevPathname.includes(path))) {
            return;
        }

        this.currentAppDef = undefined;
        const id = m.appId;
        await this.loadApplication(id);
    }

    @action.bound
    navigateToList() {
        this.rootStore.navigateToApplicationDefinitionsPage(this.currentProject);
    }

    createInputBinding(inputId: string, field?: FormField): InputBinding {
        if (field === undefined || field.type === undefined || field.value === undefined) {
            return {
                inputId,
                type: BindingType.constant,
                value: null
            };
        }

        return {
            inputId,
            type: field.type,
            value:
                field.type === BindingType.tag && field.value
                    ? this.ruleStore.ruleTags.find(y => y.name === field.value)?.id
                    : field.value
        };
    }

    @action.bound
    async save(inputs: Dictionary<FormField[]>) {
        try {
            const bindings: InputBinding[] = Object.entries(inputs).reduce(
                (acc, [inputId, fields]) => [...acc, ...fields.map(field => this.createInputBinding(inputId, field))],
                []
            );

            await this.appDefService.updateBindings(
                this.currentProject.id,
                this.currentAppDef!.id,
                bindings,
                this.getFieldSettingsPayload()
            );

            this.setIsFormChanged(false);
            this.navigateToList();
        } catch (err) {
            this.errorStore.addError(err);
        }
    }

    @action.bound
    previewApplication() {
        if (!this.currentAppDef) {
            return;
        }

        const inputBindings = Object.values(this.localBindings)
            .reduce((acc, bindings) => [...acc, ...bindings], [])
            .filter(binding => !!binding.value);

        this.previewStore.setPreviewData(this.currentAppDef.id, inputBindings);

        this.routerStore.pushToHistory(
            IotaAppPagesNavigation.ApplicationDefinitionPreview.replace(':id', this.currentProject.id).replace(
                ':appId',
                this.currentAppDef.id
            )
        );
    }

    @action
    previewInput(inputId: string, index: number) {
        if (!this.currentAppDef) {
            return;
        }

        const inputBinding = this.localBindings[inputId][index];

        if (!inputBinding) {
            return;
        }

        this.previewStore.setPreviewData(this.currentAppDef.id, [inputBinding]);

        this.routerStore.pushToHistory(
            IotaAppPagesNavigation.ApplicationDefinitionInputBindingPreview.replace(':id', this.currentProject.id)
                .replace(':appId', this.currentAppDef.id)
                .replace(':inputId', inputBinding.inputId)
                .replace(':index', index.toString())
        );
    }

    isTagDisabled(tag: string) {
        const rules = this.ruleStore.rules.filter(r => r.tag === tag);
        return !rules.length ? false : !rules.some(r => r.state === 'Enabled');
    }

    @action
    exportAppBindings() {
        if (!this.currentAppDef || !this.currentProject) {
            console.error('No current app def or project');
            return;
        }

        this.appDefService.exportAppBindings(this.currentAppDef.id, this.currentProject!.id);
    }

    @action.bound
    setIsImportingBindings(isImporting: boolean) {
        this.isImportingBindings = isImporting;
    }

    @action.bound
    handleImportResponse(response: ImportInputsResponse) {
        if (response.importedSuccessfully) {
            const appDefId = this.currentAppDef!.id;
            this.currentAppDef = undefined;

            // Needed for correct UI update
            setTimeout(() => {
                message.success('You have successfully updated the application Inputs bindings.');
                this.setCurrentImportFile(undefined);
                this.loadApplication(appDefId);
            }, 0);
            return;
        }

        if (!response.canProceed) {
            message.error(response.responseMessage);
            return;
        }

        if (response.canProceed) {
            this.setIsImportConfirmDialogVisible(true);
        }
    }

    @action.bound
    setIsImportConfirmDialogVisible(visible: boolean) {
        this.isImportConfirmDialogVisible = visible;
    }

    @action.bound
    handleImportDialogCancel() {
        this.setIsImportConfirmDialogVisible(false);
        this.setCurrentImportFile(undefined);
    }

    @action.bound
    async handleImportDialogConfirm() {
        try {
            this.setIsImportingBindings(true);
            const resp = await this.appDefService.commitAppBindings(
                this.currentAppDef!.id,
                this.currentProject!.id,
                this.currentImportFile!
            );

            if (resp.isOk()) {
                const appDefId = this.currentAppDef!.id;
                this.currentAppDef = undefined;

                // Needed for correct UI update
                setTimeout(() => {
                    this.loadApplication(appDefId);
                    this.handleImportDialogCancel();
                    message.success('You have successfully updated the application Inputs bindings.');
                }, 0);
            } else {
                message.error(resp.error.data);
                console.error(resp.error.data);
            }
        } catch (err) {
            message.error(err);
            console.error(err);
        } finally {
            this.setIsImportingBindings(false);
        }
    }

    @action.bound
    setCurrentAppDef(appDef: ApplicationDefinitionBase | undefined) {
        this.resetApplicationQueryDefinitions();
        this.currentAppDef = appDef;
        this.closeFieldSettingsDrawer();
    }

    @action.bound
    setIsFormChanged(isChanged: boolean) {
        this.isFormChanged = isChanged;
    }

    private processMeta() {
        if (this.currentAppDef) {
            this.inputs = JSON.parse(this.currentAppDef!.meta).inputs;
            this.fieldSettings = this.fieldSettingsInputs.reduce<FieldSettingsMap>((acc, input) => {
                acc[input.id] = this.createInitialFieldSettings(input);
                return acc;
            }, {});
        } else {
            this.inputs = [];
            this.fieldSettings = {};
        }
    }

    @action.bound
    private resetApplicationQueryDefinitions(): void {
        this.applicationQueryDefinitionsRequestId++;
        this.applicationQueryDefinitions = [];
        this.applicationQueryDefinitionsApplicationId = null;
        this.applicationQueryDefinitionsError = null;
        this.isApplicationQueryDefinitionsLoading = false;
    }

    private normalizeFieldSettings(settings: FieldSettings): FieldSettings {
        const defaultIsRequired = settings.defaultIsRequired ?? false;
        const requiredOverride = !settings.isVisible
            ? undefined
            : settings.requiredOverride !== undefined
              ? settings.requiredOverride
              : settings.isRequired === defaultIsRequired
                ? undefined
                : settings.isRequired;

        return {
            isVisible: settings.isVisible,
            isRequired: settings.isVisible ? (requiredOverride ?? defaultIsRequired) : false,
            defaultIsRequired,
            requiredOverride
        };
    }

    private getInputDefaultRequired(input: InputMeta): boolean {
        const required = input.validation?.required;

        if (typeof required === 'boolean') {
            return required;
        }

        if (typeof required === 'string') {
            return required === 'true';
        }

        if (required && typeof required === 'object' && 'value' in required) {
            const validationValue = (required as { value?: unknown }).value;

            if (typeof validationValue === 'boolean') {
                return validationValue;
            }

            if (typeof validationValue === 'string') {
                return validationValue === 'true';
            }
        }

        return false;
    }

    private createInitialFieldSettings(input: InputMeta): FieldSettings {
        const savedFieldSettings = this.currentAppDef?.inputFieldSettings.find(setting => setting.inputId === input.id);
        const defaultIsRequired = this.getInputDefaultRequired(input);
        const hasRequiredOverride =
            savedFieldSettings != null &&
            Object.prototype.hasOwnProperty.call(savedFieldSettings, 'required') &&
            savedFieldSettings.required != null;

        return this.normalizeFieldSettings({
            isVisible: savedFieldSettings?.hidden != null ? !savedFieldSettings.hidden : true,
            isRequired: hasRequiredOverride ? !!savedFieldSettings?.required : defaultIsRequired,
            defaultIsRequired,
            requiredOverride: hasRequiredOverride ? !!savedFieldSettings?.required : undefined
        });
    }

    private flattenAdditionalClassificationInputs(
        additionalClassifications: AdditionalClassificationDto[]
    ): InputMeta[] {
        return additionalClassifications.reduce<InputMeta[]>((acc, classification) => {
            if (!classification.classificationSlot) {
                return acc;
            }

            acc.push({
                id: classification.classificationSlot,
                name: classification.displayName,
                description: classification.systemName,
                type: 'string',
                section: 'Additional Classifications'
            });

            return [...acc, ...this.flattenAdditionalClassificationInputs(classification.children ?? [])];
        }, []);
    }
}
